import { useEffect } from "react";
import { siteConfig } from "../config";
import type { Locale, RouteKind, SeoContent } from "../content/types";
import { localizedPaths } from "./paths";

type SeoInput = {
  locale: Locale;
  routeKind: RouteKind;
  seo: SeoContent;
};

function absoluteUrl(path: string) {
  return `${siteConfig.siteUrl}${path === "/" ? "/" : path}`;
}

function setMeta(selector: string, attrs: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attrs).forEach(([key, value]) => element.setAttribute(key, value));
}

function setLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let element = document.head.querySelector<HTMLLinkElement>(selector);
  if (!element) {
    element = document.createElement("link");
    element.rel = rel;
    if (hreflang) element.hreflang = hreflang;
    document.head.appendChild(element);
  }
  element.href = href;
}

// og:locale 三语映射,写成显式表而不是三目,意图一眼可辨
const ogLocaleByLocale: Record<Locale, string> = {
  en: "en_US",
  zh: "zh_CN",
  "zh-Hant": "zh_TW"
};

// 分享卡片图三语各一张。设计帧在 Moodshot.pen:
// 「OG ZH · 分享卡片」「OG EN · 分享卡片」「OG ZH-Hant · 分享卡片」,1200×630。
// 繁体那张是从简体帧复制后只改标题「让音乐读懂它」→「讓音樂讀懂它」,
// 导出用 scale: 1(Pencil 默认 2x,会出 2400×1260 对不上另外两张)。
const ogImageFileByLocale: Record<Locale, string> = {
  en: "og-en.png",
  zh: "og-zh.png",
  "zh-Hant": "og-zh-Hant.png"
};

export function usePageSeo({ locale, routeKind, seo }: SeoInput) {
  useEffect(() => {
    const currentPath = localizedPaths[routeKind][locale];
    const enPath = localizedPaths[routeKind].en;
    const zhPath = localizedPaths[routeKind].zh;
    const zhHantPath = localizedPaths[routeKind]["zh-Hant"];
    const canonical = absoluteUrl(currentPath);

    document.documentElement.lang = locale;
    document.title = seo.title;
    setMeta('meta[name="description"]', { name: "description", content: seo.description });
    setMeta('meta[property="og:title"]', { property: "og:title", content: seo.title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: seo.description });
    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    // 分享卡片的大图。设计帧在 Moodshot.pen 的「OG ZH/EN · 分享卡片」,1200×630。
    // 必须是绝对地址:相对路径很多平台抓不到
    const ogImage = absoluteUrl(`/images/${ogImageFileByLocale[locale]}`);
    setMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });
    setMeta('meta[property="og:image:width"]', { property: "og:image:width", content: "1200" });
    setMeta('meta[property="og:image:height"]', { property: "og:image:height", content: "630" });
    setMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: seo.title });
    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Moodshot" });
    setMeta('meta[property="og:locale"]', {
      property: "og:locale", content: ogLocaleByLocale[locale],
    });
    // Twitter 认自己那套;给了 summary_large_image 才会用大图而不是小方图
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: seo.title });
    setMeta('meta[name="twitter:description"]', {
      name: "twitter:description", content: seo.description,
    });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });
    setLink("canonical", canonical);
    setLink("alternate", absoluteUrl(enPath), "en");
    setLink("alternate", absoluteUrl(zhPath), "zh");
    setLink("alternate", absoluteUrl(zhHantPath), "zh-Hant");
    setLink("alternate", absoluteUrl(enPath), "x-default");
  }, [locale, routeKind, seo.description, seo.title]);
}
